/**
 * 🛥️ ADMIN YACHT SERVICE - Admin creates yachts for hosts
 * Allows admins to add yachts to any host's fleet
 */

import { getApiBaseUrl } from '@/config/environment';

export interface CreateYachtForHostRequest {
  hostId: string;
  yacht: {
    // Required fields
    name: string;

    // Basic yacht info
    type?: string; // e.g., 'luxury', 'sport', etc.
    capacity?: number; // Max passengers (required by API)
    guests?: number; // Guest capacity for display
    description?: string;
    yachtType?: 'motor_yacht' | 'sailing_yacht' | 'catamaran' | 'luxury_yacht' | 'sport_yacht';
    year?: number;
    manufacturer?: string;
    model?: string;
    registrationNumber?: string;
    homeport?: string;
    marina?: string;
    features?: string[]; // e.g., ["Air Conditioning", "WiFi", "Jet Ski"]

    // Specifications (flat in yacht object)
    length?: string; // e.g., "55ft" - STRING with unit
    crew?: number;
    bedrooms?: number;
    bathrooms?: number;
    speed?: string; // e.g., "25 knots" - STRING with unit

    // Captain info (flat in yacht object)
    captainName?: string;
    captainExperience?: string; // e.g., "15 years"
    captainLanguages?: string[]; // e.g., ["English", "French", "Spanish"]
  };

  // Separate top-level objects
  location?: {
    country?: string;
    city?: string;
    state?: string;
    marina?: string; // Marina location
  };

  pricing?: {
    hourly?: number;
    halfDay?: number;
    fullDay?: number;
    fullDayHours?: number; // Number of hours for full day
    weekly?: number;
    securityDeposit?: number;
    currency?: string; // Default: NGN
    fuelPolicy?: 'included' | 'pay_as_you_go' | 'full_tank_return';
    cateringAvailable?: boolean;
    cateringCostPerPerson?: number;
  };

  insurance?: {
    hasInsurance?: boolean;
    type?: string;
    expiryDate?: string; // YYYY-MM-DD format
    hasSafetyEquipment?: boolean;
    safetyCertifications?: string[];
  };

  availability?: {
    instantBooking?: boolean;
    minimumRentalHours?: number;
  };
}

export interface CreateYachtResponse {
  success: boolean;
  message?: string;
  data?: {
    yachtId: string;
    shortId: string;
    hostId: string;
    yacht: any;
    location: any;
    pricing: any;
    insurance: any;
    availability: any;
    status: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  };
  error?: string;
}

export interface UploadYachtPhotosRequest {
  photos: {
    imageData: string; // base64
    type: 'exterior' | 'interior' | 'cabin' | 'deck' | 'other';
    fileName: string;
    isPrimary?: boolean;
    caption?: string;
  }[];
}

export interface UploadYachtPhotosResponse {
  success: boolean;
  message?: string;
  data?: {
    uploadedCount: number;
    photos: any[];
  };
  error?: string;
}

export const adminYachtService = {
  /**
   * 📥 Get yacht images
   * Backend endpoint: GET /api/v1/admin/yachts/{yachtId}/photos
   */
  async getYachtImages(yachtId: string): Promise<{
    success: boolean;
    data?: Array<{
      id: string;
      url: string;
      category: string;
      type?: string;
      isPrimary: boolean;
      createdAt: string;
    }>;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/yachts/${yachtId}/photos`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data?.photos || result.data || result
      };
    } catch (error) {
      console.error('❌ Error fetching yacht images:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch images'
      };
    }
  },

  /**
   * 🗑️ Delete yacht image
   * Backend endpoint: DELETE /api/v1/admin/yachts/{yachtId}/photos/{photoId}
   */
  async deleteYachtImage(yachtId: string, photoId: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl()}/api/v1/admin/yachts/${yachtId}/photos/${photoId}`;


      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Delete failed ${response.status}:`, errorText);

        let errorMessage = `Failed to delete (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message || 'Photo deleted successfully'
      };
    } catch (error) {
      console.error('❌ Error deleting yacht image:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete image'
      };
    }
  },

  /**
   * ⭐ Update primary image
   * Backend endpoint: PUT /api/v1/admin/yachts/{yachtId}/photos/{photoId}/set-primary
   */
  async setPrimaryYachtImage(yachtId: string, photoId: string): Promise<{
    success: boolean;
    message?: string;
    data?: any;
    error?: string;
  }> {
    try {

      const url = `${getApiBaseUrl()}/api/v1/admin/yachts/${yachtId}/photos/${photoId}/set-primary`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Set primary failed ${response.status}:`, errorText);

        let errorMessage = `Failed to set primary image (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message || 'Cover image updated successfully',
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error setting primary image:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to set primary image'
      };
    }
  },

  /**
   * 🛥️ Admin creates a yacht for a specific host
   */
  async createYachtForHost(data: CreateYachtForHostRequest): Promise<CreateYachtResponse> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/yachts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create Yacht API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to create yacht (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message,
        data: result.data
      };
    } catch (error: any) {
      console.error('❌ Error creating yacht for host:', error);
      return {
        success: false,
        error: error.message || 'Network error while creating yacht'
      };
    }
  },

  /**
   * 📤 Upload yacht images
   * Backend endpoint: POST /api/v1/admin/yachts/{yachtId}/photos
   */
  async uploadYachtImages(
    yachtId: string,
    images: Array<{ url: string; category: string; isPrimary: boolean }>
  ): Promise<{ success: boolean; data?: any; message?: string; error?: string }> {
    try {

      const getAdminUserId = (): string => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            return user.id || user.userId || 'admin-default';
          } catch (e) {
          }
        }
        return 'admin-default';
      };

      const adminUserId = getAdminUserId();

      const requestBody = {
        adminUserId: adminUserId,
        photos: images.map((img, index) => ({
          imageData: img.url, // base64 string with data:image/jpeg;base64, prefix
          categoryId: img.category,
          isPrimary: img.isPrimary,
          displayOrder: index
        }))
      };


      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/yachts/${yachtId}/photos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Upload failed ${response.status}:`, errorText);

        let errorMessage = `Upload failed (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error) {
      console.error('❌ Error uploading yacht images:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload images'
      };
    }
  }
};

export default adminYachtService;
